import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { formatCurrency, formatDate } from "@/lib/constants";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, UserPlus, X, SlidersHorizontal, PanelLeftOpen } from "lucide-react";
import { LeadSearchInput } from "@/components/crm/LeadSearchInput";
import { Badge } from "@/components/ui/badge";
import AdvancedPatientSearch from "@/components/crm/AdvancedPatientSearch";
import PatientQueueRail from "@/components/crm/PatientQueueRail";

const EMPTY_FILTERS = { source: "all", branch_id: "all", group: "all", date_from: "", date_to: "", has_balance: false };

// Shift a YYYY-MM-DD by n days on the local calendar.
const shiftDay = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const todayISO = () => shiftDay(new Date().toISOString().slice(0, 10), 0);

const REGISTERED_PRESETS = [
  { label: "Today", range: () => ({ from: todayISO(), to: todayISO() }) },
  { label: "Last 7 days", range: () => ({ from: shiftDay(todayISO(), -6), to: todayISO() }) },
  { label: "Last 30 days", range: () => ({ from: shiftDay(todayISO(), -29), to: todayISO() }) },
];

const PAGE_SIZE = 60;

export default function Patients() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [branches, setBranches] = useState([]);
  const [groups, setGroups] = useState([]);
  const [creating, setCreating] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // The day's queue sits beside the list, the way Ray does it — reception opens
  // this page to work through today, not to browse the whole patient base.
  // Shares the toggle with the chart page so it follows you between the two.
  const [queueOpen, setQueueOpen] = useState(
    () => localStorage.getItem("patientQueueOpen") !== "0");
  const toggleQueue = (open) => {
    setQueueOpen(open);
    localStorage.setItem("patientQueueOpen", open ? "1" : "0");
  };

  const setFilter = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const setRange = (from, to) => { setPage(1); setFilters((f) => ({ ...f, date_from: from, date_to: to })); };
  const clearFilters = () => { setPage(1); setFilters(EMPTY_FILTERS); setSearch(""); };
  const hasActiveFilters = search.trim() !== ""
    || Object.entries(filters).some(([k, v]) => v !== EMPTY_FILTERS[k]);

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit: PAGE_SIZE };
    if (search.trim()) params.search = search.trim();
    if (filters.source !== "all") params.source = filters.source;
    if (filters.branch_id !== "all") params.branch_id = filters.branch_id;
    if (filters.group !== "all") params.group = filters.group;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filters.has_balance) params.has_balance = true;
    api.get("/patients", { params })
      .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [search, filters, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    api.get("/patient-groups").then((r) => setGroups(r.data || [])).catch(() => {});
  }, []);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className={`grid gap-5 ${queueOpen ? "xl:grid-cols-[230px_minmax(0,1fr)]" : ""}`}>
      {queueOpen && <PatientQueueRail currentId={null} onClose={() => toggleQueue(false)} />}

      <div className="min-w-0 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search name, mobile or patient ID…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-10 bg-white pl-9" data-testid="patients-search" />
        </div>
        <div className="flex items-center gap-2">
          {!queueOpen && (
            <Button variant="outline" onClick={() => toggleQueue(true)} data-testid="show-queue-btn">
              <PanelLeftOpen className="mr-1.5 h-4 w-4" /> Today's queue
            </Button>
          )}
          <Button variant="outline" onClick={() => setAdvancedOpen(true)} data-testid="advanced-search-btn">
            <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Advanced
          </Button>
          <Button onClick={() => setCreating(true)} className="bg-navy text-white hover:bg-navy-hover" data-testid="new-walkin-btn">
            <UserPlus className="mr-1.5 h-4 w-4" /> New Walk-in Patient
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-navy/10 bg-white px-3 py-2.5">
        <Select value={filters.source} onValueChange={(v) => setFilter("source", v)}>
          <SelectTrigger className="h-9 w-36 bg-white" data-testid="patients-filter-source">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="walk_in">Walk-in</SelectItem>
            <SelectItem value="lead">From a lead</SelectItem>
          </SelectContent>
        </Select>

        {branches.length > 1 && (
          <Select value={filters.branch_id} onValueChange={(v) => setFilter("branch_id", v)}>
            <SelectTrigger className="h-9 w-40 bg-white" data-testid="patients-filter-branch">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All branches</SelectItem>
              {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {groups.length > 0 && (
          <Select value={filters.group} onValueChange={(v) => setFilter("group", v)}>
            <SelectTrigger className="h-9 w-40 bg-white" data-testid="patients-filter-group">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All groups</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.name} value={g.name}>{g.name} ({g.count})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <div className="inline-flex rounded-lg border border-navy/10 p-0.5">
          {REGISTERED_PRESETS.map((p) => {
            const r = p.range();
            const active = filters.date_from === r.from && filters.date_to === r.to;
            return (
              <button
                key={p.label}
                onClick={() => setRange(active ? "" : r.from, active ? "" : r.to)}
                className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${active ? "bg-navy text-white" : "text-muted-foreground hover:text-ink"}`}
                data-testid={`patients-range-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Registered</span>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilter("date_from", e.target.value)}
            className="h-9 w-36 bg-white" title="Registered from" data-testid="patients-filter-from" />
          <span className="text-xs text-muted-foreground">to</span>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilter("date_to", e.target.value)}
            className="h-9 w-36 bg-white" title="Registered to" data-testid="patients-filter-to" />
        </div>

        <label className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <input type="checkbox" checked={filters.has_balance}
            onChange={(e) => setFilter("has_balance", e.target.checked)}
            data-testid="patients-filter-balance" />
          Owes money
        </label>

        {hasActiveFilters && (
          <Button variant="ghost" onClick={clearFilters} className="h-9 px-3 text-xs text-muted-foreground hover:text-ink" data-testid="patients-filter-clear">
            Clear filters
          </Button>
        )}

        <span className="ml-auto text-sm text-muted-foreground" data-testid="patients-total">
          {loading ? "Loading…" : `${total} patient${total !== 1 ? "s" : ""}`}
        </span>
      </div>

      {/* Three across only once there is room for it. With the queue rail open
          the third column squeezed every card enough to truncate the patient's
          name, which is the one thing on the card that has to be readable. */}
      <div className={`grid gap-4 sm:grid-cols-2 ${queueOpen ? "2xl:grid-cols-3" : "lg:grid-cols-3"}`}>
        {items.length === 0 ? (
          <div className="col-span-full rounded-xl border border-navy/10 bg-white py-16 text-center text-muted-foreground">
            {loading ? "Loading…" : hasActiveFilters ? "No patients match these filters" : "No patients yet"}
          </div>
        ) : items.map((p) => (
          <button key={p.id} onClick={() => navigate(`/patients/${p.id}`)} className="rounded-xl border border-navy/10 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-gold hover:shadow-md" data-testid={`patient-card-${p.id}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-navy/10 font-display text-lg font-semibold text-navy">{p.name?.charAt(0)}</div>
              <div className="min-w-0">
                <p className="truncate font-semibold text-ink">{p.name}</p>
                <p className="text-xs text-muted-foreground">{maskPhone(p.mobile)}</p>
                {p.patient_no && <p className="font-mono text-[11px] text-muted-foreground">{p.patient_no}</p>}
              </div>
              {p.walk_in && <span className="ml-auto rounded-full bg-nude px-2 py-0.5 text-[10px] font-semibold text-navy">Walk-in</span>}
            </div>
            {/* Collected, not patient.total_revenue — that sums treatment
                amounts, so a consultation-only patient read ₹0 after paying. */}
            <div className="mt-4 flex items-center justify-between border-t pt-3">
              <span className="text-xs text-muted-foreground">Collected</span>
              <span className="font-display text-lg font-semibold text-gold">{formatCurrency(p.money?.collected ?? 0)}</span>
            </div>
            {p.money?.outstanding > 0 && (
              <p className="mt-0.5 text-right text-xs font-medium text-red-600">{formatCurrency(p.money.outstanding)} due</p>
            )}
            <p className="mt-1 text-xs text-muted-foreground">Since {formatDate(p.created_at)}</p>
          </button>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-3">
          <Button variant="outline" size="sm" disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)} data-testid="patients-prev-page">Previous</Button>
          <span className="text-sm text-ink">Page {page} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)} data-testid="patients-next-page">Next</Button>
        </div>
      )}

      </div>

      <WalkInDialog open={creating} onClose={() => setCreating(false)} onDone={load} />
      <AdvancedPatientSearch open={advancedOpen} onClose={() => setAdvancedOpen(false)} />
    </div>
  );
}

function WalkInDialog({ open, onClose, onDone }) {
  const empty = {
    name: "", mobile: "", alt_mobile: "", email: "", gender: "", dob: "", age: "",
    blood_group: "", city: "", address: "", pincode: "", occupation: "", language: "",
    referred_by: "", groups: "", abha_id: "", family_relation: "",
    sms_opt_in: true, email_opt_in: true, lead_id: "",
  };
  const [form, setForm] = useState(empty);
  const [linkedLead, setLinkedLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [bloodGroups, setBloodGroups] = useState([]);
  // Age OR date of birth, the way Ray asks it. dob is the durable fact, so when
  // it is given the age field is derived and locked rather than left to drift.
  const [ageMode, setAgeMode] = useState("age");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(empty); setLinkedLead(null); setShowMore(false); setAgeMode("age");
    api.get("/master-data", { params: { field: "blood_group" } })
      .then((r) => setBloodGroups((r.data.blood_group || []).map((b) => b.value)))
      .catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const derivedAge = React.useMemo(() => {
    if (!form.dob) return null;
    const b = new Date(form.dob);
    if (Number.isNaN(b.getTime())) return null;
    const t = new Date();
    return t.getFullYear() - b.getFullYear()
      - (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate()) ? 1 : 0);
  }, [form.dob]);

  const handleLeadSelect = (lead) => {
    setForm((f) => ({
      ...f,
      lead_id: lead.id,
      name: lead.name || f.name,
      mobile: lead.mobile || f.mobile,
      email: lead.email || f.email,
      gender: lead.gender || f.gender,
      age: lead.age ? String(lead.age) : f.age,
      city: lead.city || f.city,
    }));
    setLinkedLead(lead);
  };

  const handleLeadClear = () => {
    setForm((f) => ({ ...f, lead_id: "" }));
    setLinkedLead(null);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.mobile.trim()) { toast.error("Name and mobile are required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/patients", {
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        alt_mobile: form.alt_mobile.trim() ? [form.alt_mobile.trim()] : [],
        email: form.email.trim(),
        gender: form.gender,
        dob: form.dob || null,
        age: form.age ? Number(form.age) : null,
        blood_group: form.blood_group,
        city: form.city.trim(),
        address: form.address.trim(),
        pincode: form.pincode.trim(),
        occupation: form.occupation.trim(),
        language: form.language.trim(),
        referred_by: form.referred_by.trim(),
        // Comma-separated in the box, a list on the record — groups are how the
        // patient base gets segmented for recalls and campaigns.
        groups: form.groups.split(",").map((g) => g.trim()).filter(Boolean),
        abha_id: form.abha_id.trim(),
        family_relation: form.family_relation.trim(),
        sms_opt_in: form.sms_opt_in,
        email_opt_in: form.email_opt_in,
        lead_id: form.lead_id || null,
        walk_in: !form.lead_id,
      });
      toast.success(form.lead_id ? "Patient created from lead" : "Walk-in patient created");
      onClose();
      onDone();
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-navy">New Patient Registration</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {linkedLead && (
            <Badge variant="secondary" className="gap-1">
              Linked: {linkedLead.friendly_id || linkedLead.name}
              <button onClick={handleLeadClear} className="ml-1 rounded-sm hover:text-destructive"><X className="h-3 w-3" /></button>
            </Badge>
          )}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <div className="col-span-2 md:col-span-1">
              <label className="text-xs font-medium text-muted-foreground">Name *</label>
              <LeadSearchInput value={form.name} onChange={(v) => set("name", v)} onLeadSelect={handleLeadSelect} placeholder="Search leads or type name..." data-testid="walkin-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Mobile *</label>
              <LeadSearchInput value={form.mobile} onChange={(v) => set("mobile", v)} onLeadSelect={handleLeadSelect} lockedValue={linkedLead?.mobile} placeholder="Search by mobile..." data-testid="walkin-mobile" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Alternate mobile</label>
              <Input value={form.alt_mobile} onChange={(e) => set("alt_mobile", e.target.value)} />
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground">Gender</label>
              <Select value={form.gender || "__none__"} onValueChange={(v) => set("gender", v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  <SelectItem value="Female">Female</SelectItem>
                  <SelectItem value="Male">Male</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setAgeMode("dob")}
                  className={`text-xs font-medium ${ageMode === "dob" ? "text-navy underline" : "text-muted-foreground"}`}>DOB</button>
                <span className="text-xs text-muted-foreground">or</span>
                <button type="button" onClick={() => setAgeMode("age")}
                  className={`text-xs font-medium ${ageMode === "age" ? "text-navy underline" : "text-muted-foreground"}`}>Age</button>
              </div>
              {ageMode === "dob" ? (
                <Input type="date" value={form.dob} onChange={(e) => set("dob", e.target.value)} data-testid="walkin-dob" />
              ) : (
                <Input type="number" value={form.age} onChange={(e) => set("age", e.target.value)} placeholder="Years" />
              )}
              {ageMode === "dob" && derivedAge !== null && (
                <p className="mt-0.5 text-[11px] text-muted-foreground">{derivedAge} years old</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Blood group</label>
              <Select value={form.blood_group || "__none__"} onValueChange={(v) => set("blood_group", v === "__none__" ? "" : v)}>
                <SelectTrigger className="mt-0.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unknown</SelectItem>
                  {bloodGroups.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 md:col-span-2">
              <label className="text-xs font-medium text-muted-foreground">Email</label>
              <Input value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">City</label>
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
          </div>

          <button type="button" onClick={() => setShowMore((s) => !s)}
            className="text-xs font-medium text-navy hover:underline" data-testid="walkin-more">
            {showMore ? "− Fewer details" : "+ Address, occupation, referral & preferences"}
          </button>

          {showMore && (
            <div className="grid grid-cols-2 gap-3 rounded-lg bg-cream/40 p-3 md:grid-cols-3">
              <div className="col-span-2 md:col-span-3">
                <label className="text-xs font-medium text-muted-foreground">Street address</label>
                <Input value={form.address} onChange={(e) => set("address", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Pincode</label>
                <Input value={form.pincode} onChange={(e) => set("pincode", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Occupation</label>
                <Input value={form.occupation} onChange={(e) => set("occupation", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Language</label>
                <Input value={form.language} onChange={(e) => set("language", e.target.value)} placeholder="e.g. Marathi" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Referred by</label>
                <Input value={form.referred_by} onChange={(e) => set("referred_by", e.target.value)} />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">ABHA ID</label>
                <Input value={form.abha_id} onChange={(e) => set("abha_id", e.target.value)}
                       placeholder="14-digit ABHA" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Family relation</label>
                <Input value={form.family_relation} onChange={(e) => set("family_relation", e.target.value)}
                       placeholder="self / spouse / child" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-medium text-muted-foreground">Groups</label>
                <Input value={form.groups} onChange={(e) => set("groups", e.target.value)} placeholder="VIP, Staff (comma separated)" />
              </div>
              <div className="col-span-2 flex items-center gap-5 md:col-span-3">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={form.sms_opt_in} onChange={(e) => set("sms_opt_in", e.target.checked)} />
                  WhatsApp / SMS reminders
                </label>
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input type="checkbox" checked={form.email_opt_in} onChange={(e) => set("email_opt_in", e.target.checked)} />
                  Email reminders
                </label>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-walkin-btn">{saving ? "Saving…" : "Create Patient"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
